import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconCheck, IconColumns } from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";

export type SubmissionsFieldOption = {
  id: string;
  label: string;
};

export function SubmissionsFieldsPicker({
  fields,
  hiddenIds,
  onChange,
}: {
  fields: SubmissionsFieldOption[];
  hiddenIds: Set<string>;
  onChange: (hiddenIds: Set<string>) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [draftIds, setDraftIds] = useState<Set<string>>(() => new Set());

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setDraftIds(
      new Set(
        fields
          .filter((field) => !hiddenIds.has(field.id))
          .map((field) => field.id),
      ),
    );
  }, [fields, hiddenIds, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return fields;
    return fields.filter((field) => field.label.toLowerCase().includes(needle));
  }, [fields, query]);

  if (fields.length === 0) return null;

  const toggle = (fieldId: string) => {
    setDraftIds((current) => {
      const next = new Set(current);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const save = () => {
    onChange(
      new Set(
        fields
          .filter((field) => !draftIds.has(field.id))
          .map((field) => field.id),
      ),
    );
    close();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-8 max-w-[16rem] items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 font-onest text-xs font-medium text-slate-700 hover:bg-slate-200/80"
      >
        <IconColumns size={14} className="shrink-0 text-slate-500" />
        <span className="truncate">Показ полей</span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Показ полей"
          className="absolute left-0 z-50 mt-1.5 w-[20rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <div className="flex items-center gap-2 p-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Поиск по столбцам"
              className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-onest text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
            <button
              type="button"
              onClick={() => setQuery("")}
              className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 font-onest text-sm font-medium text-slate-700 hover:bg-slate-200/80"
            >
              Очистить
            </button>
          </div>

          <ul className="max-h-56 overflow-y-auto px-1 pb-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center font-onest text-sm text-slate-500">
                Ничего не найдено
              </li>
            ) : (
              filtered.map((field) => {
                const selected = draftIds.has(field.id);
                return (
                  <li key={field.id}>
                    <button
                      type="button"
                      onClick={() => toggle(field.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {selected ? (
                          <IconCheck size={16} className="text-brand-500" />
                        ) : (
                          <span className="h-3.5 w-3.5 rounded-sm border border-slate-300" />
                        )}
                      </span>
                      <span
                        className="min-w-0 truncate font-onest text-sm text-slate-800"
                        title={field.label}
                      >
                        {field.label}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-3">
            <button
              type="button"
              onClick={close}
              className="rounded-lg bg-slate-100 py-2 font-onest text-sm font-medium text-slate-700 hover:bg-slate-200/80"
            >
              Отменить
            </button>
            <button
              type="button"
              onClick={save}
              className="rounded-lg bg-brand-500 py-2 font-onest text-sm font-medium text-white hover:bg-brand-600"
            >
              Сохранить
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
